import { env } from '../../../utils/env';
import { AtendimentoMensagem } from '../../../services/atendimentoService';

export type LocationMessage = {
  lat: number;
  lng: number;
  name?: string | null;
  address?: string | null;
};

export type SharedContactMessage = {
  name: string;
  number?: string | null;
  email?: string | null;
};

export type ReactionMessage = {
  emoji: string;
  quotedWid?: string | null;
};

export interface PixMessage {
  kind?: string;
  amount?: number;
  currency?: string;
  note?: string;
  pixKey?: string;
  keyType?: string;
  merchantName?: string;
  label: string;
}

const parseDadosJson = (msg: AtendimentoMensagem) => (
  typeof msg.dados_json === 'string'
    ? (() => { try { return JSON.parse(msg.dados_json as any); } catch { return null; } })()
    : msg.dados_json
);

export const getMediaSrc = (msg: AtendimentoMensagem): string | null => {
  const dadosJson = parseDadosJson(msg);
  const rawBase64 = dadosJson?.mediaBase64 || dadosJson?.message?.mediaBase64;

  if (rawBase64 && (msg.media_tipo || msg.media_url?.startsWith('base64:'))) {
    const mimetype = msg.media_tipo || msg.media_url?.replace('base64:', '') || 'image/jpeg';
    return rawBase64.startsWith('data:') ? rawBase64 : `data:${mimetype};base64,${rawBase64}`;
  }

  if (!msg.media_url) return null;

  if (msg.media_url.startsWith('data:') || msg.media_url.startsWith('http') || msg.media_url.startsWith('blob:')) {
    return msg.media_url;
  }

  if (msg.media_url.startsWith('/uploads/')) {
    return `${env.API_URL.replace(/\/$/, '')}${msg.media_url}`;
  }

  return `${env.API_URL.replace(/\/$/, '')}/uploads/${msg.media_url.replace(/^\/+/, '')}`;
};

export const getFileNameFromUrl = (url: string): string | null => {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const filename = pathname.split('/').filter(Boolean).pop();
    return filename ? decodeURIComponent(filename) : null;
  } catch {
    const filename = url.split('?')[0].split('/').filter(Boolean).pop();
    return filename || null;
  }
};

export const formatFileSize = (size?: number | null): string | null => {
  if (!size || !Number.isFinite(size)) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const downloadMediaFile = async (src: string, filename: string) => {
  try {
    const response = await fetch(src);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  } catch {
    const link = document.createElement('a');
    link.href = src;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const parseLocationMessage = (msg: AtendimentoMensagem): LocationMessage | null => {
  const toCoordinate = (value: unknown): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return Number(value);
    return Number(value);
  };

  const normalize = (value: any): LocationMessage | null => {
    if (!value || typeof value !== 'object') return null;

    const lat = toCoordinate(value?.lat ?? value?.latitude ?? value?.degreesLatitude);
    const lng = toCoordinate(value?.lng ?? value?.longitude ?? value?.degreesLongitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    return {
      lat,
      lng,
      name: value?.name || value?.title || null,
      address: value?.address || null,
    };
  };

  const dadosJson = parseDadosJson(msg);

  if (msg.corpo) {
    try {
      const parsed = JSON.parse(msg.corpo);
      const location = normalize(parsed);
      if (location) return location;
    } catch {
      const latLongMatch = msg.corpo.match(/Lat:\s*(-?\d+(?:\.\d+)?)\s*,\s*Long:\s*(-?\d+(?:\.\d+)?)/i);
      if (latLongMatch) {
        return {
          lat: Number(latLongMatch[1]),
          lng: Number(latLongMatch[2]),
          name: null,
          address: null,
        };
      }

      const match = msg.corpo.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
      if (match) {
        return {
          lat: Number(match[1]),
          lng: Number(match[2]),
          name: null,
          address: null,
        };
      }
    }
  }

  return (
    normalize(dadosJson?.locationMessage) ||
    normalize(dadosJson?.message?.locationMessage) ||
    normalize(dadosJson?.sharedLocation) ||
    normalize(dadosJson?.rawData?.message?.locationMessage) ||
    normalize(dadosJson)
  );
};

export const getMapsUrl = (location: LocationMessage) => (
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${location.lat},${location.lng}`)}`
);

const getVcardValue = (vcard: string, field: string): string | null => {
  const line = vcard
    .split(/\r?\n/)
    .find((item) => item.toUpperCase().startsWith(`${field.toUpperCase()}:`) || item.toUpperCase().startsWith(`${field.toUpperCase()};`));

  if (!line) return null;
  const value = line.slice(line.indexOf(':') + 1).trim();
  return value || null;
};

export const parseContactMessage = (msg: AtendimentoMensagem): SharedContactMessage | null => {
  const normalize = (value: any): SharedContactMessage | null => {
    if (!value) return null;

    const vcard = typeof value?.vcard === 'string' ? value.vcard : null;
    const name = value?.name || value?.displayName || value?.formattedName || (vcard ? getVcardValue(vcard, 'FN') : null);
    const waid = vcard?.match(/waid=([\d]+)/i)?.[1] || null;
    const tel = vcard ? getVcardValue(vcard, 'TEL') : null;
    const email = value?.email || (vcard ? getVcardValue(vcard, 'EMAIL') : null);
    const number = value?.number || value?.phone || waid || tel?.replace(/[^\d+]/g, '') || null;

    if (!name && !number && !email) return null;

    return {
      name: name || number || 'Contato',
      number,
      email,
    };
  };

  if (msg.corpo) {
    try {
      const parsed = JSON.parse(msg.corpo);
      const contact = normalize(parsed);
      if (contact) return contact;
    } catch {
      const contact = normalize({ vcard: msg.corpo });
      if (contact) return contact;
    }
  }

  const dadosJson = parseDadosJson(msg);

  return (
    normalize(dadosJson?.contactMessage) ||
    normalize(dadosJson?.message?.contactMessage) ||
    normalize(dadosJson?.sharedContact) ||
    normalize(dadosJson)
  );
};

export const getWhatsAppContactUrl = (number: string) => `https://wa.me/${number.replace(/\D/g, '')}`;

export const parseReactionMessage = (msg: AtendimentoMensagem): ReactionMessage | null => {
  const dadosJson = parseDadosJson(msg);
  const reactionData =
    dadosJson?.reactionMessage ||
    dadosJson?.message?.reactionMessage ||
    dadosJson?.rawData?.message?.reactionMessage;

  const emoji = msg.corpo || reactionData?.text || reactionData?.emoji || '';
  const quotedWid =
    reactionData?.key?.id ||
    reactionData?.quotedWid ||
    dadosJson?.message?.reactionMessage?.key?.id ||
    dadosJson?.rawData?.message?.reactionMessage?.key?.id ||
    dadosJson?.quotedWid ||
    null;

  if (!emoji) return null;

  return { emoji, quotedWid };
};

export const parsePixMessage = (msg: AtendimentoMensagem): PixMessage | null => {
  const dados = parseDadosJson(msg);
  const pix = dados?.pix || dados?.message?.pix || null;
  const kindLabels: Record<string, string> = {
    request: 'Cobrança via Pix',
    send: 'Pagamento enviado',
    cancel: 'Pagamento cancelado',
    decline: 'Pagamento recusado',
    payment: 'Pagamento via Pix',
    unknown: 'Pix',
  };

  if (!pix && msg.tipo !== 'pix') return null;

  return {
    kind: pix?.kind,
    amount: typeof pix?.amount === 'number' ? pix.amount : undefined,
    currency: pix?.currency || 'BRL',
    note: pix?.note,
    pixKey: pix?.pixKey,
    keyType: pix?.keyType,
    merchantName: pix?.merchantName,
    label: kindLabels[pix?.kind as string] || 'Pix',
  };
};

export const formatPixAmount = (amount?: number, currency?: string): string | null => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return null;
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency || 'BRL' }).format(amount);
  } catch {
    return `${currency || ''} ${amount.toFixed(2)}`.trim();
  }
};

export const getQuotedWidFromMessage = (msg: AtendimentoMensagem): string | null => {
  const dadosJson = parseDadosJson(msg);
  const content = dadosJson?.message || dadosJson;
  const contentType = content
    ? Object.keys(content).find((key) => key.endsWith('Message') || key === 'conversation')
    : null;
  const contextInfo = contentType ? content?.[contentType]?.contextInfo : null;

  return (
    dadosJson?.quotedWid ||
    contextInfo?.stanzaId ||
    content?.extendedTextMessage?.contextInfo?.stanzaId ||
    content?.imageMessage?.contextInfo?.stanzaId ||
    content?.videoMessage?.contextInfo?.stanzaId ||
    content?.documentMessage?.contextInfo?.stanzaId ||
    null
  );
};

export const getMensagemPreviewText = (msg: AtendimentoMensagem | null): string => {
  if (!msg) return 'Mensagem';
  if (msg.deletada) return 'Mensagem apagada';
  if (msg.corpo?.trim()) {
    return msg.corpo.trim()
      .replace(/\[sticker\]/gi, '[figurinha]')
      .replace(/\[document\]/gi, '[documento]')
      .replace(/\[location\]/gi, '[localização]')
      .replace(/\[contact\]/gi, '[contato]');
  }

  const labels: Record<string, string> = {
    image: 'Imagem',
    video: 'Vídeo',
    audio: 'Áudio',
    document: msg.media_nome || 'Documento',
    sticker: 'Figurinha',
    location: 'Localização',
    contact: 'Contato',
    reaction: 'Reação',
    pix: 'Pix',
    private: 'Nota privada',
    text: 'Mensagem',
  };

  return labels[msg.tipo] || 'Mensagem';
};

export const getQuotedMessagePreviewText = (msg: AtendimentoMensagem, quotedMessage?: AtendimentoMensagem | null): string => {
  if (quotedMessage) return getMensagemPreviewText(quotedMessage);

  const dadosJson = parseDadosJson(msg);
  const content = dadosJson?.message || dadosJson;
  const contentType = content
    ? Object.keys(content).find((key) => key.endsWith('Message') || key === 'conversation')
    : null;
  const contextInfo = contentType ? content?.[contentType]?.contextInfo : null;
  const quotedMsg = contextInfo?.quotedMessage;

  if (!quotedMsg) return 'Mensagem';

  const text = quotedMsg.conversation || quotedMsg.extendedTextMessage?.text;
  if (text) {
    return text
      .replace(/\[sticker\]/gi, '[figurinha]')
      .replace(/\[document\]/gi, '[documento]')
      .replace(/\[location\]/gi, '[localização]')
      .replace(/\[contact\]/gi, '[contato]');
  }

  if (quotedMsg.imageMessage) return 'Imagem';
  if (quotedMsg.videoMessage) return 'Vídeo';
  if (quotedMsg.audioMessage) return 'Áudio';
  if (quotedMsg.documentMessage) return quotedMsg.documentMessage.fileName || 'Documento';
  if (quotedMsg.stickerMessage) return 'Figurinha';
  if (quotedMsg.locationMessage) return 'Localização';
  if (quotedMsg.contactMessage) return 'Contato';

  return 'Mensagem';
};

export const buildReactionsByWid = (mensagens: AtendimentoMensagem[]) => (
  mensagens.reduce<Map<string, ReactionMessage[]>>((acc, msg, index) => {
    if (msg.tipo !== 'reaction') return acc;

    const reaction = parseReactionMessage(msg);
    if (!reaction) return acc;

    const fallbackTarget = [...mensagens]
      .slice(0, index)
      .reverse()
      .find((item) => item.tipo !== 'reaction')?.wid;

    const targetWid = reaction.quotedWid || fallbackTarget;
    if (!targetWid) return acc;

    const current = acc.get(targetWid) || [];
    acc.set(targetWid, [...current, { ...reaction, quotedWid: targetWid }]);
    return acc;
  }, new Map())
);
